import { memo, useMemo } from "react";
import { GS, usePrefersReducedMotion } from "./utils";

/**
 * Shared CSS/SVG decoration primitives for the Golden Serenity template.
 * All decoration is recreated with inline SVG — no raster assets are shipped
 * from the prototype (unknown licensing).
 */

/** Organic hand-drawn divider: wavy gold lines around a sage dot. */
export const OrganicDivider = memo(({ width = 60 }: { width?: number }) => (
  <div className="flex items-center justify-center gap-3" aria-hidden="true">
    <svg width={width} height="2" style={{ color: GS.gold }}>
      <path
        d={`M0,1 Q${width / 4},0 ${width / 2},1 T${width},1`}
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
        opacity="0.4"
      />
    </svg>
    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GS.sage }} />
    <svg width={width} height="2" style={{ color: GS.gold }}>
      <path
        d={`M0,1 Q${width / 4},0 ${width / 2},1 T${width},1`}
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
        opacity="0.4"
      />
    </svg>
  </div>
));
OrganicDivider.displayName = "OrganicDivider";

/** Thin gold gradient hairline used under labels. */
export const GoldHairline = memo(({ className = "" }: { className?: string }) => (
  <div
    aria-hidden="true"
    className={`h-px mx-auto opacity-30 ${className}`}
    style={{ width: "2.5rem", background: `linear-gradient(to right, transparent, ${GS.gold}, transparent)` }}
  />
));
GoldHairline.displayName = "GoldHairline";

/** Corner botanical sprays (olive branch / eucalyptus), pure SVG. */
export const BotanicalCorner = memo(
  ({ position }: { position: "top-left" | "top-right" | "bottom-left" | "bottom-right" }) => {
    const positionClass = {
      "top-left": "top-0 left-0",
      "top-right": "top-0 right-0 -scale-x-100",
      "bottom-left": "bottom-0 left-0 -scale-y-100",
      "bottom-right": "bottom-0 right-0 -scale-x-100 -scale-y-100",
    }[position];

    return (
      <div
        aria-hidden="true"
        className={`absolute w-40 h-40 sm:w-64 sm:h-64 pointer-events-none ${positionClass}`}
      >
        <svg viewBox="0 0 200 200" fill="none" className="w-full h-full">
          <path d="M10 10 Q40 30 60 70 T80 130" stroke={GS.sage} strokeWidth="2" fill="none" opacity="0.6" />
          <ellipse cx="35" cy="35" rx="12" ry="6" fill={GS.sage} opacity="0.5" transform="rotate(-30 35 35)" />
          <ellipse cx="45" cy="50" rx="14" ry="7" fill={GS.sageDeep} opacity="0.6" transform="rotate(-20 45 50)" />
          <ellipse cx="52" cy="68" rx="13" ry="6" fill={GS.sage} opacity="0.5" transform="rotate(-35 52 68)" />
          <ellipse cx="60" cy="85" rx="15" ry="7" fill={GS.sageDeep} opacity="0.55" transform="rotate(-25 60 85)" />
          <ellipse cx="68" cy="105" rx="12" ry="6" fill={GS.sage} opacity="0.5" transform="rotate(-40 68 105)" />
          <ellipse cx="75" cy="122" rx="14" ry="7" fill={GS.sageDeep} opacity="0.6" transform="rotate(-30 75 122)" />
          <circle cx="42" cy="42" r="3" fill={GS.gold} opacity="0.7" />
          <circle cx="40" cy="40" r="1.5" fill={GS.goldWarm} opacity="0.8" />
          <circle cx="65" cy="92" r="3.5" fill={GS.gold} opacity="0.65" />
          <circle cx="63" cy="90" r="1.5" fill={GS.goldWarm} opacity="0.8" />
        </svg>
      </div>
    );
  },
);
BotanicalCorner.displayName = "BotanicalCorner";

/** Warm golden-hour ambient light blobs. */
export const AmbientGlow = memo(() => (
  <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute top-0 right-0 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] rounded-full blur-[120px]"
      style={{ background: "rgba(212,165,116,0.10)" }}
    />
    <div
      className="absolute bottom-0 left-0 w-[340px] h-[340px] sm:w-[600px] sm:h-[600px] rounded-full blur-[140px]"
      style={{ background: "rgba(201,168,106,0.08)" }}
    />
  </div>
));
AmbientGlow.displayName = "AmbientGlow";

type BokehDot = {
  id: number;
  left: number;
  top: number;
  size: number;
  opacity: number;
  delay: number;
  duration: number;
};

/** Deterministic pseudo-random generator so dots stay stable across renders. */
function seededRandom(seed: number) {
  let value = seed;
  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
}

/**
 * Soft floating golden bokeh dots. Animation is disabled entirely when the
 * visitor prefers reduced motion.
 */
export const BokehBackground = memo(() => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const dots = useMemo<BokehDot[]>(() => {
    const rand = seededRandom(11);
    return Array.from({ length: 24 }, (_, i) => ({
      id: i,
      left: rand() * 100,
      top: rand() * 100,
      size: rand() * 8 + 4,
      opacity: rand() * 0.25 + 0.08,
      delay: rand() * 5,
      duration: rand() * 3 + 4,
    }));
  }, []);

  return (
    <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {dots.map((dot) => (
        <div
          key={dot.id}
          className="absolute rounded-full"
          style={{
            left: `${dot.left}%`,
            top: `${dot.top}%`,
            width: `${dot.size}px`,
            height: `${dot.size}px`,
            opacity: dot.opacity,
            backgroundColor: GS.gold,
            animation: prefersReducedMotion
              ? "none"
              : `gs-float ${dot.duration}s ease-in-out ${dot.delay}s infinite alternate`,
            filter: "blur(2px)",
          }}
        />
      ))}
    </div>
  );
});
BokehBackground.displayName = "BokehBackground";
